/**
 * @author        matt@zumper.com (Matthieu Devlin)
 * @copyright     Copyright (c) 2024, Zumper
 * @description   Sentry initialization for browser
 */

import { makeMultiplexedTransport } from '@sentry/core'
import {
  captureConsoleIntegration,
  init,
  makeFetchTransport,
  reactRouterV5BrowserTracingIntegration,
  replayIntegration,
} from '@sentry/react'
import type { RouterHistory } from '@sentry/react/build/types/reactrouter'
import { selectIsBot, selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { APP_HOSTNAME } from '@zcode/nr-utils'
import type { State } from '@zcode/state-type'
import { denyUrlArray, sentryErrorArray } from '../../server/utils/sentryErrors'
import { parameterizeXfrPath } from '../parameterizeXfrPath'
import { dsnFromEvent } from './dsnFromEvent'

const PROPAGATION_TARGET_REGEX = new RegExp(`^https://${APP_HOSTNAME}`)

declare global {
  interface Window {
    __SENTRY__: {
      dsn: string
      thirdPartyDsn: string
      tracesSampleRate: number
      replaysSessionSampleRate: number
      replaysOnErrorSampleRate: number
      environment: string
    }
    __GIT_SHA__: string
  }
}

export const initSentry = (history: RouterHistory, state: State) =>
  init({
    dsn: window.__SENTRY__.dsn,
    attachStacktrace: true,
    denyUrls: denyUrlArray,
    tracePropagationTargets: [PROPAGATION_TARGET_REGEX],
    integrations: [
      captureConsoleIntegration({ levels: ['error'] }),
      reactRouterV5BrowserTracingIntegration({
        history,
        enableInp: true,
        beforeStartSpan: (context) => {
          // want to convert XFR routes to parameterized names like on server
          const path = window.location.pathname
          const name = parameterizeXfrPath(path) || context.name || path
          return { ...context, name }
        },
      }),
      replayIntegration({
        // NOTE: This will disable built-in masking. Only use this if your site has no sensitive data, or if you've already set up other options for masking or blocking relevant data, such as 'ignore', 'block', 'mask' and 'maskFn'.
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: window.__SENTRY__.tracesSampleRate,
    replaysSessionSampleRate: window.__SENTRY__.replaysSessionSampleRate,
    replaysOnErrorSampleRate: window.__SENTRY__.replaysOnErrorSampleRate,
    environment: window.__SENTRY__.environment,
    release: window.__GIT_SHA__,
    ignoreErrors: sentryErrorArray,
    initialScope: (scope) => {
      scope.setTags({
        is_bot: selectIsBot(state),
        is_mobile: selectIsMobile(state),
      })
      return scope
    },
    transport: makeMultiplexedTransport(
      makeFetchTransport,
      dsnFromEvent(window.__SENTRY__.dsn, window.__SENTRY__.thirdPartyDsn, window.__GIT_SHA__)
    ),
  })
